import React, { useState, useEffect } from "react";
import { Icon } from "@iconify/react";
import Card from "../Card";
import { Typography } from "../Typography";
import { getIdeaMentorPreferences, getAllIdeaUsers } from "../../Utils/apiServices";

const STATUS_STYLES = {
  ACCEPTED: { bg: "bg-green-50", text: "text-green-700", border: "border-green-200", dot: "bg-green-500", label: "Accepted" },
  REJECTED: { bg: "bg-red-50", text: "text-red-700", border: "border-red-200", dot: "bg-red-500", label: "Rejected" },
  PENDING: { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200", dot: "bg-amber-500", label: "Pending" },
};

const mentorName = (mentor, mentorId) => {
  if (!mentor) return `Mentor ${mentorId ?? ""}`.trim();
  const name = `${mentor.first_name || ""} ${mentor.last_name || ""}`.trim();
  return name || mentor.email || `Mentor ${mentor.id ?? ""}`.trim();
};

const mentorSubtitle = (mentor) => {
  if (!mentor) return "";
  return mentor.profession || mentor.organization || mentor.email || "";
};

const formatDate = (value) => {
  if (!value) return "";
  try {
    return new Date(value).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "";
  }
};

/**
 * Admin-facing panel about an idea's mentors.
 *
 * While the idea is still awaiting a decision (PENDING / not yet active) it lists
 * the student's mentor preferences (1/2/3) in order, each with the mentor,
 * decision status, and — when a mentor declined — the rejection reason.
 *
 * Once the idea has been accepted (status ACTIVE), the preference list is no
 * longer meaningful: the panel instead shows the "Active Mentorship" — the
 * mentor(s) actually mentoring the idea. That's the accepted primary mentor
 * plus any secondary (co-)mentors, which live on the idea's team members
 * (idea_users) rather than in the preference list.
 */
const MentorPreferencesPanel = ({
  ideaId,
  ideaStatus = "",
  rejectionReason = "",
  className = "",
}) => {
  const [preferences, setPreferences] = useState([]);
  const [secondaryMentors, setSecondaryMentors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const statusUpper = String(ideaStatus || "").toUpperCase();
  const isRejected = statusUpper === "REJECTED";
  // Team members (secondary mentors) are only relevant once an idea has been
  // accepted — while it's active, or after it was later rejected at a stage.
  const needsTeam = statusUpper === "ACTIVE" || isRejected;

  // The accepted preference is the primary (active) mentor. An idea only becomes
  // active once one of the student's preferences accepts it.
  const acceptedPreference = preferences.find(
    (p) => String(p.status || "").toUpperCase() === "ACCEPTED"
  );
  // The "Active Mentorship" section is shown separately from (and below) the
  // preference list once a mentor has accepted — while the idea is active, and
  // also after it was later rejected during mentoring. An idea rejected during
  // the preference stage (no mentor ever accepted) shows no active section.
  const showActiveMentorship =
    statusUpper === "ACTIVE" || (isRejected && !!acceptedPreference);
  // Whether the primary mentor rejected the idea after accepting it (a stage
  // rejection). Its reason is the idea-level reason, shown in the active section.
  const primaryRejected = isRejected && !!acceptedPreference;
  const hasActiveMentors = !!acceptedPreference || secondaryMentors.length > 0;

  useEffect(() => {
    let active = true;
    if (!ideaId) {
      setLoading(false);
      return undefined;
    }
    (async () => {
      try {
        setLoading(true);
        setError("");
        // Preferences give us the primary mentor; once the idea is active (or was
        // active and later rejected) we also pull the team members to surface any
        // secondary (co-)mentors.
        const [prefRes, teamRes] = await Promise.all([
          getIdeaMentorPreferences(ideaId),
          needsTeam
            ? getAllIdeaUsers({ idea_id: ideaId })
            : Promise.resolve(null),
        ]);
        const list = prefRes?.data?.data ?? prefRes?.data ?? [];
        if (active) setPreferences(Array.isArray(list) ? list : []);

        const teamRows = teamRes?.data?.data ?? teamRes?.data ?? [];
        const secondaries = Array.isArray(teamRows)
          ? teamRows.filter((row) => row?.role === "SECONDARY_MENTOR")
          : [];
        if (active) setSecondaryMentors(secondaries);
      } catch (err) {
        console.error("Error loading mentor preferences:", err);
        if (active) setError("Could not load mentor preferences.");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [ideaId, needsTeam]);

  const reasonBox = (label, reason, date) => (
    <div className="rounded-lg border border-red-100 bg-red-50 px-3 py-2">
      <Typography as="p" className="text-xs font-semibold text-red-700">
        {label}
      </Typography>
      <Typography as="p" className="text-sm text-red-800 mt-0.5">
        {reason}
      </Typography>
      {date && formatDate(date) && (
        <Typography as="p" className="text-[11px] text-red-500 mt-1">
          Declined on {formatDate(date)}
        </Typography>
      )}
    </div>
  );

  const statusBadge = (statusKey) => {
    const style = STATUS_STYLES[statusKey] || STATUS_STYLES.PENDING;
    return (
      <span
        className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${style.bg} ${style.text} ${style.border}`}
      >
        <span className={`h-1.5 w-1.5 rounded-full ${style.dot}`} />
        {style.label}
      </span>
    );
  };

  return (
    <Card className={`border border-slate-200 p-5 ${className}`}>
      {/* ── Mentor Preferences: the student's ranked mentor choices ── */}
      <div className="flex items-center gap-2 mb-4">
        <Icon icon="lucide:users" className="h-5 w-5 text-violet-600" />
        <Typography as="h3" className="text-base font-semibold text-primary-900">
          Mentor Preferences
        </Typography>
      </div>

      {loading ? (
        <Typography as="p" className="text-sm text-tertiary-600">
          Loading mentor preferences...
        </Typography>
      ) : error ? (
        <Typography as="p" className="text-sm text-red-600">
          {error}
        </Typography>
      ) : preferences.length === 0 ? (
        <Typography as="p" className="text-sm text-tertiary-600">
          The student hasn&apos;t selected any preferred mentors for this idea yet.
        </Typography>
      ) : (
        <div className="flex flex-col gap-3">
          {preferences.map((pref) => {
            const status = String(pref.status || "PENDING").toUpperCase();
            const isCurrentlyReviewing = status === "PENDING" && pref.assigned_at;
            // Only surface a rejection reason for a preference that actually
            // declined and left one.
            const showReason = status === "REJECTED" && pref.decision_reason;
            return (
              <div
                key={pref.id}
                className="flex flex-col gap-2 rounded-xl border border-slate-200 bg-white p-4"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-violet-100 text-sm font-semibold text-violet-700">
                      {`#${pref.preference_order}`}
                    </span>
                    <div className="flex flex-col min-w-0">
                      <Typography as="span" className="text-sm font-semibold text-primary-900 truncate">
                        {mentorName(pref.mentor, pref.mentor_id)}
                      </Typography>
                      {mentorSubtitle(pref.mentor) && (
                        <Typography as="span" className="text-xs text-tertiary-600 truncate">
                          {mentorSubtitle(pref.mentor)}
                        </Typography>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    {statusBadge(status)}
                    {isCurrentlyReviewing && (
                      <span className="inline-flex items-center gap-1 text-[11px] font-medium text-blue-600">
                        <Icon icon="lucide:clock" className="h-3 w-3" />
                        Currently reviewing
                      </span>
                    )}
                  </div>
                </div>

                {showReason &&
                  reasonBox("Reason for rejection", pref.decision_reason, pref.decided_at)}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Active Mentorship: the mentors actually mentoring the idea ── */}
      {!loading && !error && showActiveMentorship && (
        <div className="mt-6 border-t border-slate-100 pt-5">
          <div className="flex items-center gap-2 mb-4">
            <Icon icon="lucide:user-check" className="h-5 w-5 text-violet-600" />
            <Typography as="h3" className="text-base font-semibold text-primary-900">
              Active Mentorship
            </Typography>
          </div>

          {!hasActiveMentors ? (
            <Typography as="p" className="text-sm text-tertiary-600">
              No mentor is actively mentoring this idea yet.
            </Typography>
          ) : (
            <div className="flex flex-col gap-3">
              {acceptedPreference && (
                <div className="flex flex-col gap-2 rounded-xl border border-slate-200 bg-white p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-violet-100 text-sm font-semibold text-violet-700">
                        <Icon icon="lucide:user-check" className="h-4 w-4" />
                      </span>
                      <div className="flex flex-col min-w-0">
                        <Typography as="span" className="text-sm font-semibold text-primary-900 truncate">
                          {mentorName(acceptedPreference.mentor, acceptedPreference.mentor_id)}
                        </Typography>
                        <Typography as="span" className="text-xs font-medium text-violet-600 truncate">
                          Primary Mentor
                        </Typography>
                        {mentorSubtitle(acceptedPreference.mentor) && (
                          <Typography as="span" className="text-xs text-tertiary-600 truncate">
                            {mentorSubtitle(acceptedPreference.mentor)}
                          </Typography>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      {statusBadge(primaryRejected ? "REJECTED" : "ACCEPTED")}
                    </div>
                  </div>

                  {primaryRejected &&
                    rejectionReason &&
                    reasonBox("Reason for rejection", rejectionReason)}
                </div>
              )}

              {secondaryMentors.map((row) => (
                <div
                  key={`secondary-${row.id}`}
                  className="flex flex-col gap-2 rounded-xl border border-slate-200 bg-white p-4"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-violet-100 text-sm font-semibold text-violet-700">
                        <Icon icon="lucide:user-check" className="h-4 w-4" />
                      </span>
                      <div className="flex flex-col min-w-0">
                        <Typography as="span" className="text-sm font-semibold text-primary-900 truncate">
                          {mentorName(row.user, row.user_id)}
                        </Typography>
                        <Typography as="span" className="text-xs font-medium text-violet-600 truncate">
                          Secondary Mentor
                        </Typography>
                        {mentorSubtitle(row.user) && (
                          <Typography as="span" className="text-xs text-tertiary-600 truncate">
                            {mentorSubtitle(row.user)}
                          </Typography>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      {statusBadge("ACCEPTED")}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

export default MentorPreferencesPanel;
